import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import { addMonthsIso } from '../utils/dates';
import { lineGstBreakdown } from '../utils/gst';
import * as posStore from '../utils/posLocalStore';
import SearchableCustomerSelect from '../components/SearchableCustomerSelect';
import InvoicePrint from '../components/InvoicePrint';
import ProductImage from '../components/ProductImage';
import {
  pickDefaultUom,
  effectiveSalePrice,
  toBaseQty,
  openPriceLayers,
  layerUnitPrice,
  consumePriceLayers,
  piecesFromInnerFactor,
  formatStockWithRetail,
  formatQty,
  uomSellsFractional,
  maxSellableUom,
  snapSellQty
} from '../utils/uom';
import './POS.css';

const POS = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const clientId = user?.client_id || 'default';
  const clientGstOn = Boolean(user?.gst_enabled);
  const canCreateGstBill = isAdmin && !clientGstOn;
  const [applyGstBill, setApplyGstBill] = useState(false);
  const gstEnabled = clientGstOn || (canCreateGstBill && applyGstBill);

  const [products, setProducts] = useState([]);
  const [combos, setCombos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [fromCache, setFromCache] = useState(false);
  const [heldBills, setHeldBills] = useState([]);
  const [offlineQueue, setOfflineQueue] = useState([]);
  const [showHolds, setShowHolds] = useState(false);
  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState('paid'); // paid | pending
  const [paymentMethod, setPaymentMethod] = useState('cash'); // cash | upi | credit_card
  const [printBill, setPrintBill] = useState(true);
  const [printInvoice, setPrintInvoice] = useState(null);
  const [printCustomer, setPrintCustomer] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerFormData, setCustomerFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    tax_id: '',
    status: 'active'
  });
  const searchInputRef = useRef(null);
  const cartItemsRef = useRef(null);
  const scanBufferRef = useRef({ chars: '', lastAt: 0, timer: null });
  const lastScanRef = useRef({ code: '', at: 0 });
  const [menuOpen, setMenuOpen] = useState(false);
  const syncingRef = useRef(false);

  useEffect(() => {
    setHeldBills(posStore.loadHolds(clientId));
    setOfflineQueue(posStore.loadQueue(clientId));
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when store/client changes
  }, [clientId]);

  useEffect(() => {
    const goOnline = () => {
      setOffline(false);
      syncOfflineQueue();
    };
    const goOffline = () => setOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    const probe = setInterval(async () => {
      if (document.hidden) return;
      const ok = await posStore.canReachApi(1500);
      setOffline((prev) => {
        if (prev && ok) {
          // became reachable again
          setTimeout(() => syncOfflineQueue(), 0);
        }
        return !ok;
      });
    }, 12000);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      clearInterval(probe);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Fullscreen POS: hide app sidebar by default; optional reopen
  useEffect(() => {
    document.body.classList.add('pos-mode');
    return () => {
      document.body.classList.remove('pos-mode', 'pos-sidebar-open');
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('pos-sidebar-open', menuOpen);
  }, [menuOpen]);

  // Keep newest cart lines visible when items are added
  useEffect(() => {
    const el = cartItemsRef.current;
    if (!el) return;
    el.scrollTop = 0;
  }, [cart]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsRes, customersRes, combosRes] = await Promise.all([
        axios.get('/api/products'),
        axios.get('/api/customers'),
        axios.get('/api/combos').catch(() => ({ data: { combos: [] } }))
      ]);
      const nextProducts = (productsRes.data.products || []).filter((p) => p.status === 'active');
      const nextCustomers = customersRes.data.customers || [];
      setProducts(nextProducts);
      setCustomers(nextCustomers);
      setCombos((combosRes.data.combos || []).filter((c) => c.status !== 'inactive' && (c.items || []).length >= 2));
      posStore.cacheCatalog(clientId, nextProducts, nextCustomers);
      setFromCache(false);
      setOffline(false);

      // Backfill missing uoms (older cache / products with only unit label)
      const missing = nextProducts.filter((p) => !Array.isArray(p.uoms) || p.uoms.length === 0).slice(0, 40);
      if (missing.length) {
        Promise.all(
          missing.map(async (p) => {
            try {
              const res = await axios.get(`/api/products/${p.id}/uoms`);
              return { id: p.id, uoms: res.data.uoms || [] };
            } catch (_) {
              return { id: p.id, uoms: [] };
            }
          })
        ).then((rows) => {
          const map = Object.fromEntries(rows.map((r) => [r.id, r.uoms]));
          setProducts((prev) => {
            const updated = prev.map((p) => (map[p.id] ? { ...p, uoms: map[p.id] } : p));
            posStore.cacheCatalog(clientId, updated, nextCustomers);
            return updated;
          });
        });
      }
    } catch (error) {
      console.error('POS load error:', error);
      const cached = posStore.loadCatalogCache(clientId);
      if (cached && (cached.products.length > 0 || cached.customers.length > 0)) {
        setProducts(cached.products);
        setCustomers(cached.customers);
        setFromCache(true);
        setOffline(true);
      } else {
        alert(error.response?.data?.message || 'Failed to load POS data');
      }
    } finally {
      setLoading(false);
    }
  };

  const expandCartItems = (lines) => lines.flatMap((item) => {
    if (!item.combo_id) {
      return [{
        product_id: item.product_id,
        description: item.description,
        quantity: item.quantity,
        uom_id: item.uom_id || undefined,
        conversion_to_base: item.conversion_to_base || 1,
        qty_base: toBaseQty(item.quantity, item.conversion_to_base || 1),
        unit_price: item.unit_price,
        discount: item.discount || 0,
        gst_rate: gstEnabled ? (parseFloat(item.gst_rate) || 18) : 0,
        gst_inclusive: gstEnabled ? item.gst_inclusive : false
      }];
    }
    const parts = item.components || [];
    const comboTotal = Math.max(0, item.quantity * item.unit_price - (Number(item.discount) || 0));
    const weights = parts.map((part) => {
      const factor = parseFloat(part.conversion_to_base) || 1;
      const unitList = part.uom_sale_price != null && part.uom_sale_price !== ''
        ? parseFloat(part.uom_sale_price) || 0
        : (parseFloat(part.product_price) || 0) * factor;
      return unitList * (parseFloat(part.quantity) || 1);
    });
    const weightSum = weights.reduce((sum, n) => sum + n, 0) || parts.length || 1;
    let allocated = 0;
    return parts.map((part, index) => {
      const qty = (parseFloat(part.quantity) || 1) * item.quantity;
      const factor = parseFloat(part.conversion_to_base) || 1;
      const share = index === parts.length - 1
        ? Math.round((comboTotal - allocated) * 1000) / 1000
        : Math.round(comboTotal * ((weights[index] || 0) / weightSum) * 1000) / 1000;
      allocated += share;
      const unitPrice = qty > 0 ? Math.round((share / qty) * 1000) / 1000 : 0;
      return {
        product_id: part.product_id,
        description: `${item.description}: ${qty} ${part.uom_code || 'pcs'} ${part.product_name || 'Item'}`,
        quantity: qty,
        uom_id: part.uom_id || undefined,
        conversion_to_base: factor,
        qty_base: toBaseQty(qty, factor),
        unit_price: unitPrice,
        discount: 0,
        gst_rate: gstEnabled ? (parseFloat(part.gst_rate) || 0) : 0,
        gst_inclusive: gstEnabled ? true : false
      };
    });
  });

  const paymentMethodLabel = (method) => {
    if (method === 'upi') return 'UPI';
    if (method === 'credit_card') return 'Credit card';
    return 'Cash';
  };

  const buildSalePayload = () => {
    const paid = paymentStatus === 'paid';
    const method = paid ? paymentMethod : null;
    const methodNote = paid ? paymentMethodLabel(paymentMethod) : 'Pending';
    const issue_date = new Date().toISOString().split('T')[0];
    return {
      customer_id: customerId,
      issue_date,
      due_date: addMonthsIso(issue_date, 1),
      discount: parseFloat(discount) || 0,
      notes: gstEnabled
        ? `POS Sale (${methodNote}, GST)`
        : `POS Sale (${methodNote})`,
      status: paid ? 'paid' : 'pending',
      apply_gst: gstEnabled,
      ...(paid ? { payment_method: method } : {}),
      items: expandCartItems(cart)
    };
  };

  const enqueueOfflineSale = (payload) => {
    const customer = customers.find((c) => c.id === customerId) || null;
    const entry = {
      id: posStore.newId(),
      created_at: new Date().toISOString(),
      customer_name: customer?.name || '',
      print_bill: printBill,
      payload
    };
    const next = [...posStore.loadQueue(clientId), entry];
    posStore.saveQueue(clientId, next);
    setOfflineQueue(next);
    setOffline(true);

    // Reflect stock locally so POS stays usable offline
    const sold = new Map();
    (payload.items || []).forEach((item) => {
      const id = item.product_id;
      const base =
        item.qty_base != null
          ? parseFloat(item.qty_base) || 0
          : toBaseQty(item.quantity, item.conversion_to_base || 1);
      sold.set(id, (sold.get(id) || 0) + base);
    });
    const nextProducts = products.map((p) => {
      if (!sold.has(p.id)) return p;
      const left = Math.max(0, (parseFloat(p.stock_quantity) || 0) - sold.get(p.id));
      const priceLayers = consumePriceLayers(p.price_layers, sold.get(p.id));
      const nextPrice = priceLayers[0] ? priceLayers[0].sale_price : p.price;
      return {
        ...p,
        stock_quantity: left,
        price_layers: priceLayers,
        price: priceLayers.length ? nextPrice : p.price
      };
    });
    setProducts(nextProducts);
    posStore.cacheCatalog(clientId, nextProducts, customers);

    clearCart();
    alert(`Sale saved offline (${next.length} pending).\nIt will sync automatically when you are back online.`);
  };

  const syncOfflineQueue = useCallback(async () => {
    if (syncingRef.current) return;
    const reachable = await posStore.canReachApi();
    if (!reachable) {
      setOffline(true);
      return;
    }
    const queue = posStore.loadQueue(clientId);
    if (!queue.length) {
      setOfflineQueue([]);
      return;
    }
    syncingRef.current = true;
    const remaining = [];
    let synced = 0;
    let lastPrint = null;

    for (const entry of queue) {
      try {
        const response = await axios.post('/api/invoices', entry.payload, { timeout: 20000 });
        const saved = response.data.invoice;
        synced += 1;
        if (entry.print_bill && saved?.id) {
          lastPrint = {
            invoiceId: saved.id,
            customerId: entry.payload.customer_id,
            customerName: entry.customer_name
          };
        }
      } catch (err) {
        if (posStore.isQueueableError(err)) {
          const idx = queue.indexOf(entry);
          remaining.push(...queue.slice(idx));
          break;
        }
        // Validation error — drop so it does not block the queue forever
        console.warn('Dropping offline sale that failed validation:', err?.response?.data || err);
      }
    }

    posStore.saveQueue(clientId, remaining);
    setOfflineQueue(remaining);
    syncingRef.current = false;

    if (synced > 0) {
      try {
        const [productsRes, customersRes] = await Promise.all([
          axios.get('/api/products'),
          axios.get('/api/customers')
        ]);
        const nextProducts = (productsRes.data.products || []).filter((p) => p.status === 'active');
        const nextCustomers = customersRes.data.customers || [];
        setProducts(nextProducts);
        setCustomers(nextCustomers);
        posStore.cacheCatalog(clientId, nextProducts, nextCustomers);
        setFromCache(false);
        setOffline(false);

        if (lastPrint) {
          const fullResponse = await axios.get(`/api/invoices/${lastPrint.invoiceId}`);
          setPrintInvoice(fullResponse.data.invoice);
          const cust =
            nextCustomers.find((c) => c.id === lastPrint.customerId) ||
            { id: lastPrint.customerId, name: lastPrint.customerName };
          setPrintCustomer(cust);
        } else {
          alert(`Synced ${synced} offline sale(s).`);
        }
      } catch {
        alert(`Synced ${synced} offline sale(s).`);
      }
    }
  }, [clientId]);

  useEffect(() => {
    if (!offline && offlineQueue.length > 0) {
      syncOfflineQueue();
    }
  }, [offline, offlineQueue.length, syncOfflineQueue]);

  const holdBill = () => {
    if (cart.length === 0) {
      alert('Cart is empty');
      return;
    }
    const customer = customers.find((c) => c.id === customerId) || null;
    const bill = {
      id: posStore.newId(),
      created_at: new Date().toISOString(),
      customer_id: customerId || '',
      customer_name: customer?.name || '',
      cart: cart.map((item) => ({ ...item })),
      discount: parseFloat(discount) || 0,
      payment_status: paymentStatus,
      payment_method: paymentMethod,
      print_bill: printBill,
      apply_gst_bill: applyGstBill
    };
    const next = [...posStore.loadHolds(clientId), bill];
    posStore.saveHolds(clientId, next);
    setHeldBills(next);
    clearCart();
  };

  const resumeHold = (bill) => {
    if (cart.length > 0) {
      alert('Clear or hold the current cart before resuming a held bill');
      return;
    }
    setCart(Array.isArray(bill.cart) ? bill.cart.map((item) => ({ ...item })) : []);
    setDiscount(bill.discount || 0);
    setPaymentStatus(bill.payment_status || 'paid');
    setPaymentMethod(bill.payment_method || 'cash');
    setPrintBill(bill.print_bill !== false);
    setApplyGstBill(Boolean(bill.apply_gst_bill));
    setCustomerId(bill.customer_id || '');
    const next = posStore.loadHolds(clientId).filter((h) => h.id !== bill.id);
    posStore.saveHolds(clientId, next);
    setHeldBills(next);
    setShowHolds(false);
  };

  const deleteHold = (billId) => {
    const next = posStore.loadHolds(clientId).filter((h) => h.id !== billId);
    posStore.saveHolds(clientId, next);
    setHeldBills(next);
  };

  const openAddCustomer = () => {
    setCustomerFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
      tax_id: '',
      status: 'active'
    });
    setShowCustomerModal(true);
  };

  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    if (!customerFormData.name?.trim()) {
      alert('Customer name is required');
      return;
    }
    if (!customerFormData.phone?.trim()) {
      alert('Phone / Mobile is required');
      return;
    }

    setSavingCustomer(true);
    try {
      const response = await axios.post('/api/customers', customerFormData);
      const created = response.data?.customer;
      const customersRes = await axios.get('/api/customers');
      const list = customersRes.data.customers || [];
      setCustomers(list);
      if (created?.id) {
        setCustomerId(created.id);
      }
      setShowCustomerModal(false);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save customer');
    } finally {
      setSavingCustomer(false);
    }
  };

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        p.sku?.toLowerCase().includes(q) ||
        String(p.barcode || '').toLowerCase().includes(q) ||
        p.category_name?.toLowerCase().includes(q)
    );
  }, [products, search]);

  const findProductByScanCode = (raw) => {
    const code = String(raw || '').trim();
    if (!code) return null;
    const lower = code.toLowerCase();
    for (const p of products) {
      const uoms = Array.isArray(p.uoms) ? p.uoms : [];
      const matchedUom = uoms.find(
        (u) => String(u.barcode || '').trim() === code || String(u.barcode || '').trim().toLowerCase() === lower
      );
      if (matchedUom) return { product: p, uom: matchedUom };
      if (
        String(p.barcode || '').trim() === code ||
        String(p.barcode || '').trim().toLowerCase() === lower ||
        String(p.sku || '').trim().toLowerCase() === lower
      ) {
        return { product: p, uom: pickDefaultUom(uoms, 'sales') };
      }
    }
    return null;
  };

  /** Ensure product has uoms from API (cache / old list may omit them). */
  const ensureProductUoms = async (product) => {
    if (!product?.id) return product;
    if (Array.isArray(product.uoms) && product.uoms.length > 0) return product;
    try {
      const res = await axios.get(`/api/products/${product.id}/uoms`);
      const uoms = res.data.uoms || [];
      const enriched = { ...product, uoms };
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, uoms } : p)));
      return enriched;
    } catch (_) {
      // Fallback: synthetic single UOM from product.unit so POS still shows a unit label
      const code = product.unit || 'pcs';
      const synthetic = [{
        id: `local-${product.id}-base`,
        code,
        name: code,
        is_base: 1,
        conversion_to_base: 1,
        is_sales_default: 1,
        is_purchase_default: 1,
        allow_fractional: 0
      }];
      return { ...product, uoms: synthetic };
    }
  };

  const addToCart = async (product, preferredUom = null) => {
    if (!product) return false;
    const full = await ensureProductUoms(product);
    const stockBase = parseFloat(full.stock_quantity) || 0;
    if (stockBase <= 0) {
      alert(`${full.name} is out of stock`);
      return false;
    }

    const uom = preferredUom || pickDefaultUom(full.uoms, 'sales');
    const factor = parseFloat(uom?.conversion_to_base) || 1;
    const uomId = uom?.id || null;

    let added = true;
    setCart((prev) => {
      const sameUom = prev.filter((item) => (
        !item.combo_id && item.product_id === full.id && (item.uom_id || null) === uomId
      ));
      const exceptKeys = sameUom.map(cartLineKey);
      const current = sameUom.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
      const nextQty = uomSellsFractional(uom) ? current + 1 : Math.round(current) + 1;
      const alloc = allocateUomQty(full, uom, nextQty, prev, exceptKeys);
      if (!alloc.ok) {
        alert(`Only ${formatQty(alloc.maxUom)} ${uom?.code || full.unit || ''} in stock for ${full.name}`);
        added = false;
        return prev;
      }
      const rest = prev.filter((item) => !exceptKeys.includes(cartLineKey(item)));
      const lines = alloc.parts.map((part, index) => {
        const catalogPrice = part.layer ? part.layer.sale_price : full.price;
        const unitPrice = part.layer
          ? layerUnitPrice(uom, part.layer.sale_price, full.price)
          : effectiveSalePrice(uom, full.price);
        const prevLine = sameUom[index];
        const keepOverride = prevLine && prevLine.price_overridden && (prevLine.layer_id || null) === (part.layer?.id || null);
        const lineMax = Math.round(part.qty * (keepOverride ? prevLine.unit_price : unitPrice) * 1000) / 1000;
        return {
          product_id: full.id,
          uom_id: uomId,
          layer_id: part.layer?.id || null,
          uom_code: uom?.code || full.unit || 'pcs',
          conversion_to_base: factor,
          description: full.name,
          quantity: part.qty,
          unit_price: keepOverride ? prevLine.unit_price : unitPrice,
          price_overridden: Boolean(keepOverride),
          discount: Math.min(Number(prevLine?.discount) || 0, lineMax),
          gst_rate: full.gst_rate || 0,
          gst_inclusive: Boolean(Number(full.gst_inclusive)),
          stock_quantity: stockBase,
          uoms: full.uoms || [],
          catalog_price: catalogPrice
        };
      });
      return [...lines, ...rest];
    });
    return added;
  };

  /** Barcode gun / Enter: exact barcode (or SKU) → add to cart and clear search. */
  const addScannedProductToCart = (rawCode) => {
    const code = String(rawCode || '').trim();
    if (!code) return false;

    // Guns often send Enter right after digits — avoid adding twice
    const now = Date.now();
    if (lastScanRef.current.code === code && now - lastScanRef.current.at < 500) {
      setSearch('');
      return true;
    }

    const matched = findProductByScanCode(code);
    if (matched) {
      const ok = addToCart(matched.product, matched.uom);
      if (ok) {
        lastScanRef.current = { code, at: now };
        setSearch('');
        window.clearTimeout(scanBufferRef.current.timer);
        scanBufferRef.current = { chars: '', lastAt: 0, timer: null };
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
      return ok;
    }

    alert(`No product found for barcode: ${code}`);
    setSearch('');
    window.clearTimeout(scanBufferRef.current.timer);
    scanBufferRef.current = { chars: '', lastAt: 0, timer: null };
    setTimeout(() => searchInputRef.current?.focus(), 0);
    return false;
  };

  const handleSearchKeyDown = (e) => {
    // Barcode guns type quickly and usually send Enter at the end
    if (e.key === 'Enter') {
      e.preventDefault();
      const fromBuffer = scanBufferRef.current.chars.trim();
      const code = (fromBuffer || search).trim();
      if (!code) return;

      const exact = findProductByScanCode(code);
      if (exact) {
        addScannedProductToCart(code);
        return;
      }

      // Single filtered hit (manual search + Enter) also adds
      if (filteredProducts.length === 1) {
        const ok = addToCart(filteredProducts[0]);
        if (ok) {
          setSearch('');
          setTimeout(() => searchInputRef.current?.focus(), 0);
        }
        return;
      }

      if (code) {
        alert(`No product found for barcode: ${code}`);
        setSearch('');
      }
      return;
    }

    // Track rapid keystrokes (guns without Enter suffix)
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const now = Date.now();
      const buf = scanBufferRef.current;
      if (now - buf.lastAt > 80) {
        buf.chars = e.key;
      } else {
        buf.chars += e.key;
      }
      buf.lastAt = now;

      // If a full exact barcode was typed very fast, auto-add after a short idle
      window.clearTimeout(buf.timer);
      buf.timer = window.setTimeout(() => {
        const candidate = String(buf.chars || '').trim();
        if (candidate.length >= 6 && findProductByScanCode(candidate)) {
          addScannedProductToCart(candidate);
        }
      }, 120);
    }
  };

  const cartLineKey = (item) => (
    item.combo_id
      ? `combo::${item.combo_id}`
      : `${item.product_id}::${item.uom_id || 'base'}::${item.layer_id || 'std'}`
  );

  const exceptKeysOf = (exceptKey) => {
    if (!exceptKey) return new Set();
    if (exceptKey instanceof Set) return exceptKey;
    if (Array.isArray(exceptKey)) return new Set(exceptKey);
    return new Set([exceptKey]);
  };

  const productFreeBase = (product, lines, exceptKey) => {
    const skip = exceptKeysOf(exceptKey);
    const stockBase = parseFloat(product?.stock_quantity) || 0;
    const used = (lines || []).reduce((sum, item) => {
      if (item.combo_id || item.product_id !== product.id) return sum;
      if (skip.has(cartLineKey(item))) return sum;
      return sum + toBaseQty(item.quantity, item.conversion_to_base || 1);
    }, 0);
    return Math.max(0, stockBase - used);
  };

  /** Sell from total stock as whole packs (3 bags / 23 packets), not split as 0.16 Bag lots. */
  const allocateUomQty = (product, uom, qty, lines, exceptKey) => {
    const freeBase = productFreeBase(product, lines, exceptKey);
    const maxUom = maxSellableUom(freeBase, uom);
    const want = snapSellQty(qty, uom);
    if (!(want > 0) || want > maxUom + 1e-9) {
      return { ok: false, maxUom };
    }
    const priceLayer = openPriceLayers(product || {})[0] || null;
    return { ok: true, parts: [{ layer: priceLayer, qty: want }] };
  };

  const addComboToCart = (combo) => {
    const parts = combo.items || [];
    const short = parts.find((part) => {
      const product = products.find((p) => p.id === part.product_id);
      const need = (parseFloat(part.quantity) || 1) * (parseFloat(part.conversion_to_base) || 1);
      return !product || (parseFloat(product.stock_quantity) || 0) + 1e-9 < need;
    });
    if (short) {
      alert(`Not enough stock for ${short.product_name || 'a product in this combo'}`);
      return;
    }
    setCart((prev) => {
      const existing = prev.find((item) => item.combo_id === combo.id);
      if (existing) {
        const nextQty = existing.quantity + 1;
        const blocked = parts.find((part) => {
          const product = products.find((p) => p.id === part.product_id);
          const need = (parseFloat(part.quantity) || 1) * (parseFloat(part.conversion_to_base) || 1) * nextQty;
          return (parseFloat(product?.stock_quantity) || 0) + 1e-9 < need;
        });
        if (blocked) {
          alert(`Not enough stock for ${blocked.product_name || 'a product in this combo'}`);
          return prev;
        }
        const rest = prev.filter((item) => item.combo_id !== combo.id);
        return [{ ...existing, quantity: nextQty }, ...rest];
      }
      return [{
        combo_id: combo.id,
        description: combo.name,
        quantity: 1,
        unit_price: parseFloat(combo.price) || 0,
        discount: 0,
        components: parts,
        uom_code: 'combo',
        gst_rate: 0,
        gst_inclusive: true
      }, ...prev];
    });
  };

  const updateQty = (lineKey, quantity) => {
    setCart((prev) => {
      const item = prev.find((row) => cartLineKey(row) === lineKey);
      if (!item) return prev;
      const raw = parseFloat(quantity);
      const qty = Number.isFinite(raw) && raw > 0 ? raw : 1;
      if (item.combo_id) {
        return prev.map((row) => {
          if (cartLineKey(row) !== lineKey) return row;
          const blocked = (row.components || []).find((part) => {
            const product = products.find((p) => p.id === part.product_id);
            const need = (parseFloat(part.quantity) || 1) * (parseFloat(part.conversion_to_base) || 1) * qty;
            return (parseFloat(product?.stock_quantity) || 0) + 1e-9 < need;
          });
          if (blocked) {
            alert(`Not enough stock for ${blocked.product_name || 'a product in this combo'}`);
            return row;
          }
          const lineMax = Math.round(qty * row.unit_price * 1000) / 1000;
          return { ...row, quantity: qty, discount: Math.min(Number(row.discount) || 0, lineMax) };
        });
      }

      const factor = parseFloat(item.conversion_to_base) || 1;
      const product = products.find((p) => p.id === item.product_id) || {
        id: item.product_id,
        stock_quantity: item.stock_quantity,
        price: item.catalog_price,
        uoms: item.uoms,
        price_layers: []
      };
      const uom = (item.uoms || []).find((row) => row.id === item.uom_id) || {
        conversion_to_base: factor,
        code: item.uom_code,
        allow_fractional: 0
      };
      const sameUom = prev.filter((row) => (
        !row.combo_id && row.product_id === item.product_id && (row.uom_id || null) === (item.uom_id || null)
      ));
      const exceptKeys = sameUom.map(cartLineKey);
      const otherQty = sameUom
        .filter((row) => cartLineKey(row) !== lineKey)
        .reduce((sum, row) => sum + (parseFloat(row.quantity) || 0), 0);
      const nextQty = uomSellsFractional(uom) ? otherQty + qty : Math.round(otherQty) + snapSellQty(qty, uom);
      const alloc = allocateUomQty(product, uom, nextQty, prev, exceptKeys);
      if (!alloc.ok) {
        alert(`Only ${formatQty(alloc.maxUom)} ${item.uom_code || ''} in stock`);
        return prev;
      }
      const rest = prev.filter((row) => !exceptKeys.includes(cartLineKey(row)));
      const lines = alloc.parts.map((part, index) => {
        const layerId = part.layer?.id || null;
        const keepOverride = index === 0 && item.price_overridden && (item.layer_id || null) === layerId;
        const unitPrice = keepOverride
          ? item.unit_price
          : part.layer
            ? layerUnitPrice(uom, part.layer.sale_price, product?.price ?? item.unit_price)
            : effectiveSalePrice(uom, product?.price ?? item.unit_price);
        const prevLine = sameUom[index];
        const lineMax = Math.round(part.qty * unitPrice * 1000) / 1000;
        return {
          ...item,
          layer_id: layerId,
          quantity: part.qty,
          unit_price: unitPrice,
          price_overridden: Boolean(keepOverride),
          discount: Math.min(Number(prevLine?.discount ?? item.discount) || 0, lineMax),
          catalog_price: part.layer ? part.layer.sale_price : (product?.price ?? item.catalog_price)
        };
      });
      return [...lines, ...rest];
    });
  };

  const changeLineUom = async (lineKey, uomId) => {
    setCart((prev) => {
      const item = prev.find((row) => cartLineKey(row) === lineKey);
      if (!item || item.combo_id) return prev;
      const uoms = item.uoms || [];
      const uom = uoms.find((u) => u.id === uomId);
      if (!uom) return prev;
      if ((uom.id || null) === (item.uom_id || null)) return prev;
      const factor = parseFloat(uom.conversion_to_base) || 1;
      const product = products.find((p) => p.id === item.product_id) || {
        id: item.product_id,
        stock_quantity: item.stock_quantity,
        price: item.catalog_price,
        uoms,
        price_layers: []
      };
      const sameUom = prev.filter((row) => (
        !row.combo_id && row.product_id === item.product_id && (row.uom_id || null) === (item.uom_id || null)
      ));
      const exceptKeys = sameUom.map(cartLineKey);
      const currentQty = sameUom.reduce((sum, row) => sum + (parseFloat(row.quantity) || 0), 0);
      const currentBase = toBaseQty(currentQty, item.conversion_to_base || 1);
      const equivalent = maxSellableUom(currentBase, uom);
      const maxUom = maxSellableUom(productFreeBase(product, prev, exceptKeys), uom);
      let nextQty = equivalent;
      if (!uomSellsFractional(uom) && nextQty < 1) nextQty = 1;
      if (nextQty > maxUom) nextQty = maxUom;
      const alloc = allocateUomQty(product, uom, nextQty, prev, exceptKeys);
      if (!alloc.ok) {
        alert(`Only ${formatQty(alloc.maxUom)} ${uom.code} in stock`);
        return prev;
      }
      const rest = prev.filter((row) => !exceptKeys.includes(cartLineKey(row)));
      const lines = alloc.parts.map((part) => {
        const catalogPrice = part.layer ? part.layer.sale_price : (product.price || item.catalog_price);
        const unitPrice = part.layer
          ? layerUnitPrice(uom, part.layer.sale_price, product.price ?? item.unit_price)
          : effectiveSalePrice(uom, product.price ?? item.unit_price);
        const lineMax = Math.round(part.qty * unitPrice * 1000) / 1000;
        return {
          ...item,
          uom_id: uom.id,
          uom_code: uom.code,
          conversion_to_base: factor,
          layer_id: part.layer?.id || null,
          quantity: part.qty,
          unit_price: unitPrice,
          price_overridden: false,
          discount: Math.min(Number(item.discount) || 0, lineMax),
          catalog_price: catalogPrice,
          uoms: uoms.length ? uoms : item.uoms
        };
      });
      return [...lines, ...rest];
    });
  };

  const updateLineDiscount = (lineKey, value) => {
    const raw = parseFloat(value);
    const discount = Number.isFinite(raw) && raw > 0 ? raw : 0;
    setCart((prev) =>
      prev.map((item) => {
        if (cartLineKey(item) !== lineKey) return item;
        const lineMax = Math.round(item.quantity * item.unit_price * 1000) / 1000;
        return { ...item, discount: Math.min(discount, lineMax) };
      })
    );
  };

  const updateLinePrice = (lineKey, value) => {
    const raw = parseFloat(value);
    if (!Number.isFinite(raw) || raw < 0) return;
    const unitPrice = Math.round(raw * 1000) / 1000;
    setCart((prev) =>
      prev.map((item) => {
        if (cartLineKey(item) !== lineKey) return item;
        const lineMax = Math.round(item.quantity * unitPrice * 1000) / 1000;
        return {
          ...item,
          unit_price: unitPrice,
          price_overridden: true,
          discount: Math.min(Number(item.discount) || 0, lineMax)
        };
      })
    );
  };

  const removeFromCart = (lineKey) => {
    setCart((prev) => prev.filter((item) => cartLineKey(item) !== lineKey));
  };

  const clearCart = () => {
    setCart([]);
    setDiscount(0);
    setPaymentStatus('paid');
    setApplyGstBill(false);
  };

  const totals = useMemo(() => {
    let taxable = 0;
    let taxAmount = 0;
    let cgstAmount = 0;
    let sgstAmount = 0;
    let gross = 0;
    let cgstRate = 0;
    let sgstRate = 0;

    cart.forEach((item) => {
      const breakdown = lineGstBreakdown(
        item.quantity,
        item.unit_price,
        item.discount || 0,
        gstEnabled ? (parseFloat(item.gst_rate) || 18) : 0,
        gstEnabled ? item.gst_inclusive : false
      );
      taxable += breakdown.taxable;
      taxAmount += breakdown.gst;
      cgstAmount += breakdown.cgst;
      sgstAmount += breakdown.sgst;
      gross += breakdown.lineTotal;
      if (breakdown.cgstRate) cgstRate = breakdown.cgstRate;
      if (breakdown.sgstRate) sgstRate = breakdown.sgstRate;
    });

    const disc = parseFloat(discount) || 0;
    return {
      subtotal: taxable,
      taxAmount,
      cgstAmount,
      sgstAmount,
      cgstRate,
      sgstRate,
      total: Math.max(0, gross - disc),
      itemCount: cart.reduce((sum, i) => sum + i.quantity, 0)
    };
  }, [cart, discount, gstEnabled]);

  const completeSale = async () => {
    if (!customerId) {
      alert('Please select a customer');
      return;
    }
    if (cart.length === 0) {
      alert('Cart is empty');
      return;
    }

    const payload = buildSalePayload();
    const customer = customers.find((c) => c.id === customerId) || null;

    setSaving(true);
    try {
      // Don't trust navigator.onLine alone — probe the API first
      const reachable = offline ? false : await posStore.canReachApi();
      if (!reachable) {
        setOffline(true);
        enqueueOfflineSale(payload);
        return;
      }

      const response = await axios.post('/api/invoices', payload, { timeout: 20000 });
      const saved = response.data.invoice;

      clearCart();
      setOffline(false);
      // Refresh stock; if this fails, keep working from local state
      try {
        await fetchData();
      } catch (_) {
        /* ignore */
      }

      if (printBill) {
        try {
          const fullResponse = await axios.get(`/api/invoices/${saved.id}`, { timeout: 15000 });
          const fullInvoice = fullResponse.data.invoice || saved;
          setPrintInvoice(fullInvoice);
          setPrintCustomer(customer);
        } catch (_) {
          setPrintInvoice(saved);
          setPrintCustomer(customer);
        }
      } else {
        alert(
          `Sale saved · ${saved.invoice_number || 'Invoice'}\nStock updated. Bill was not printed.`
        );
      }
    } catch (error) {
      if (posStore.isQueueableError(error)) {
        setOffline(true);
        enqueueOfflineSale(payload);
      } else {
        alert(error.response?.data?.message || error.message || 'Failed to complete sale');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="container">Loading POS...</div>
      </div>
    );
  }

  return (
    <div className="page-container pos-page">
      {menuOpen ? (
        <button
          type="button"
          className="pos-menu-backdrop"
          aria-label="Close menu"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}

      <div className="pos-header">
        <div className="pos-header-left">
          <button
            type="button"
            className="pos-menu-btn"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            title={menuOpen ? 'Hide menu' : 'Open menu'}
          >
            ☰
          </button>
          <div>
            <h1>POS Sale</h1>
            <p>Scan or tap products — cart updates on the right</p>
          </div>
        </div>
        <div className="pos-header-actions">
          {heldBills.length > 0 && (
            <button type="button" className="btn btn-secondary" onClick={() => setShowHolds(true)}>
              Held ({heldBills.length})
            </button>
          )}
          {offlineQueue.length > 0 && !offline && (
            <button type="button" className="btn btn-secondary" onClick={syncOfflineQueue}>
              Sync ({offlineQueue.length})
            </button>
          )}
          <Link to="/invoices" className="btn btn-secondary" onClick={() => setMenuOpen(false)}>
            All Invoices
          </Link>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            Dashboard
          </button>
        </div>
      </div>

      {(offline || fromCache || offlineQueue.length > 0) && (
        <div className={`pos-offline-banner ${offline || fromCache ? 'is-offline' : 'is-queue'}`}>
          {offline || fromCache
            ? `Offline${fromCache ? ' — using cached products' : ''}${offlineQueue.length ? ` · ${offlineQueue.length} sale(s) waiting to sync` : ''}`
            : `${offlineQueue.length} offline sale(s) waiting — click Sync when ready`}
        </div>
      )}

      <div className="pos-layout">
        <section className="pos-products-panel">
          <div className="pos-search-bar">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Scan barcode or search name / SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              autoFocus
              autoComplete="off"
              inputMode="search"
            />
          </div>

          {combos.length > 0 && (
            <div className="pos-combo-bar">
              <div className="pos-combo-label">Combo offers</div>
              <div className="pos-combo-list">
                {combos.map((combo) => (
                  <button
                    key={combo.id}
                    type="button"
                    className="pos-combo-card"
                    onClick={() => addComboToCart(combo)}
                  >
                    <strong>{combo.name}</strong>
                    <span>{formatCurrency(combo.price)}</span>
                    <small>
                      {(combo.items || []).map((item) => `${item.quantity} ${item.uom_code || 'pcs'} ${item.product_name}`).join(' + ')}
                    </small>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="pos-product-grid">
            {filteredProducts.length === 0 ? (
              <div className="pos-empty">No products found</div>
            ) : (
              filteredProducts.map((product) => {
                const stock = parseFloat(product.stock_quantity) || 0;
                const out = stock <= 0;
                const priceLayers = openPriceLayers(product);
                const activeLayer = priceLayers[0];
                const laterLayer = priceLayers.find((layer) => (
                  Math.abs(layer.sale_price - (activeLayer?.sale_price || 0)) > 0.009
                ));
                const showPrice = activeLayer ? activeLayer.sale_price : (product.price || 0);
                const uomCodes = (product.uoms || []).map((u) => u.code).filter(Boolean);
                const unitLabel = uomCodes.length
                  ? uomCodes.join(' / ')
                  : (product.unit || '');
                return (
                  <button
                    key={product.id}
                    type="button"
                    className={`pos-product-card ${out ? 'out-of-stock' : ''}`}
                    onClick={() => addToCart(product)}
                    disabled={out}
                  >
                    <ProductImage
                      productId={product.id}
                      hasImage={Boolean(product.has_image || product.image_filename)}
                      alt={product.name}
                      size={72}
                      className="pos-product-image"
                      cacheKey={product.updated_at || product.image_filename || ''}
                    />
                    <div className="pos-product-name">{product.name}</div>
                    <div className="pos-product-meta">
                      {product.sku ? `SKU: ${product.sku}` : product.category_name || 'Product'}
                      {product.barcode ? ` · ${product.barcode}` : ''}
                    </div>
                    <div className="pos-product-price">{formatCurrency(showPrice)}</div>
                    {laterLayer ? (
                      <div className="pos-price-queue">
                        {String(Math.round(activeLayer.qty_remaining * 1000) / 1000)} {product.unit || 'pcs'} at this price, then {formatCurrency(laterLayer.sale_price)}
                      </div>
                    ) : null}
                    <div className={`pos-product-stock ${out ? 'danger' : ''}`}>
                      {out ? 'Out of stock' : `Stock: ${formatStockWithRetail(product)}`}
                    </div>
                    {unitLabel ? (
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                        Units: {unitLabel}
                      </div>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </section>

        <aside className="pos-cart-panel">
          <div className="pos-cart-header">
            <div>
              <h2>Cart</h2>
              <span className="pos-cart-count">
                {cart.length} line{cart.length === 1 ? '' : 's'} · {totals.itemCount} item{totals.itemCount === 1 ? '' : 's'}
              </span>
            </div>
            <div className="pos-cart-header-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={holdBill}
                disabled={cart.length === 0}
                title="Park this bill and start a new one"
              >
                Hold
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={clearCart} disabled={cart.length === 0}>
                Clear
              </button>
            </div>
          </div>

          <div className="pos-customer">
            <label>Customer *</label>
            <SearchableCustomerSelect
              customers={customers}
              value={customerId}
              onChange={setCustomerId}
              onAddNew={openAddCustomer}
              required
            />
          </div>

          <div className="pos-cart-items" ref={cartItemsRef}>
            {cart.length === 0 ? (
              <div className="pos-empty">Cart is empty — scan or tap a product</div>
            ) : (
              cart.map((item) => {
                const lineKey = cartLineKey(item);
                const lineGross = item.quantity * item.unit_price;
                const lineDisc = Number(item.discount) || 0;
                const lineTotal = Math.max(0, lineGross - lineDisc);
                return (
                  <div key={lineKey} className="pos-cart-item">
                    <div className="pos-cart-item-top">
                      <div className="pos-cart-item-info">
                        <strong>{item.description}</strong>
                        <div className="pos-cart-unit-price">
                          {formatCurrency(item.unit_price)} × {formatQty(item.quantity)} {item.uom_code || ''}
                          {lineDisc > 0 ? ` − disc ${formatCurrency(lineDisc)}` : ''}
                        </div>
                      </div>
                      <button type="button" className="pos-remove" onClick={() => removeFromCart(lineKey)} title="Remove">
                        ×
                      </button>
                    </div>
                    <div className="pos-cart-item-row pos-cart-uom-row">
                      {item.combo_id ? (
                        <div className="pos-combo-contents">
                          {(item.components || []).map((part) => `${part.quantity} ${part.uom_code || 'pcs'} × ${part.product_name}`).join(' + ')}
                        </div>
                      ) : (
                      <label className="pos-uom-label">
                        <span>Unit</span>
                        <select
                          className="pos-uom-select"
                          value={
                            item.uom_id
                            || (item.uoms && item.uoms[0] && item.uoms[0].id)
                            || ''
                          }
                          onChange={(e) => changeLineUom(lineKey, e.target.value)}
                          aria-label="Unit of measure"
                        >
                          {(item.uoms || []).length > 0 ? (
                            (item.uoms || []).map((u) => (
                              <option key={u.id} value={u.id}>
                                {(() => {
                                  const baseCode = (item.uoms || []).find((x) => Number(x.is_base))?.code || 'base';
                                  const factor = parseFloat(u.conversion_to_base);
                                  if (Number(u.is_base)) return `${u.code} (base)`;
                                  if (factor > 0 && factor < 1) {
                                    const n = piecesFromInnerFactor(factor);
                                    return `${u.code} (${n} in 1 ${baseCode})`;
                                  }
                                  return `${u.code} = ${u.conversion_to_base} ${baseCode}`;
                                })()}
                              </option>
                            ))
                          ) : (
                            <option value="">{item.uom_code || 'pcs'}</option>
                          )}
                        </select>
                      </label>
                      )}
                      <div className="pos-qty">
                        <button
                          type="button"
                          onClick={() => updateQty(lineKey, (uomSellsFractional(item) ? item.quantity : Math.round(item.quantity)) - 1)}
                          aria-label="Decrease"
                        >-</button>
                        <input
                          type="number"
                          min="0"
                          step={uomSellsFractional(item) ? 'any' : '1'}
                          value={uomSellsFractional(item) ? item.quantity : Math.round(item.quantity)}
                          onChange={(e) => updateQty(lineKey, e.target.value)}
                          aria-label="Quantity"
                        />
                        <button
                          type="button"
                          onClick={() => updateQty(lineKey, (uomSellsFractional(item) ? item.quantity : Math.round(item.quantity)) + 1)}
                          aria-label="Increase"
                        >+</button>
                      </div>
                      <label className="pos-line-price" title="Changes the price on this bill only">
                        <span>Price</span>
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          value={item.unit_price}
                          onChange={(e) => updateLinePrice(lineKey, e.target.value)}
                          aria-label={`Price for ${item.description} on this bill`}
                        />
                      </label>
                      <label className="pos-line-discount">
                        <span>Disc</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.discount || 0}
                          onChange={(e) => updateLineDiscount(lineKey, e.target.value)}
                          aria-label={`Discount for ${item.description}`}
                        />
                      </label>
                      <div className="pos-cart-line-total">
                        {formatCurrency(lineTotal)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="pos-cart-footer">
            <div className="pos-footer-options">
              {canCreateGstBill && (
                <label className="pos-gst-override" title="Client GST is off — admin override for this sale">
                  <input
                    type="checkbox"
                    checked={applyGstBill}
                    onChange={(e) => setApplyGstBill(e.target.checked)}
                  />
                  <span>GST bill</span>
                </label>
              )}
              <label
                className="pos-print-option"
                title="Uncheck to save the sale and update stock without opening the print dialog"
              >
                <input
                  type="checkbox"
                  checked={printBill}
                  onChange={(e) => setPrintBill(e.target.checked)}
                />
                <span>Print bill</span>
              </label>
              <div className="pos-payment-fields">
                <div className="pos-payment-status">
                  <label htmlFor="pos-payment-status">Payment</label>
                  <select
                    id="pos-payment-status"
                    value={paymentStatus}
                    onChange={(e) => setPaymentStatus(e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
                {paymentStatus === 'paid' && (
                  <div className="pos-payment-status">
                    <label htmlFor="pos-payment-method">Pay by</label>
                    <select
                      id="pos-payment-method"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="credit_card">Credit card</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="pos-totals">
              <div className="pos-total-row">
                <span>Subtotal</span>
                <span>{formatCurrency(totals.subtotal)}</span>
              </div>
              {gstEnabled && (
                <>
                  <div className="pos-total-row">
                    <span>CGST{totals.cgstRate ? ` (${totals.cgstRate}%)` : ''}</span>
                    <span>{formatCurrency(totals.cgstAmount)}</span>
                  </div>
                  <div className="pos-total-row">
                    <span>SGST{totals.sgstRate ? ` (${totals.sgstRate}%)` : ''}</span>
                    <span>{formatCurrency(totals.sgstAmount)}</span>
                  </div>
                </>
              )}
              <div className="pos-total-row">
                <label htmlFor="pos-discount">Bill discount</label>
                <input
                  id="pos-discount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                />
              </div>
              <div className="pos-total-row grand">
                <span>Total</span>
                <span>{formatCurrency(totals.total)}</span>
              </div>
            </div>

            <button
              type="button"
              className="btn btn-primary pos-checkout-btn"
              onClick={completeSale}
              disabled={saving || cart.length === 0}
            >
              {saving
                ? 'Processing...'
                : printBill
                  ? (paymentStatus === 'paid'
                    ? `Complete & Print (${paymentMethodLabel(paymentMethod)})`
                    : 'Complete & Print (Pending)')
                  : (paymentStatus === 'paid'
                    ? `Complete Sale (${paymentMethodLabel(paymentMethod)})`
                    : 'Complete Sale (Pending)')}
            </button>
          </div>
        </aside>
      </div>

      {printInvoice && (
        <InvoicePrint
          invoice={printInvoice}
          customer={printCustomer}
          autoPrint
          onClose={() => {
            setPrintInvoice(null);
            setPrintCustomer(null);
          }}
        />
      )}

      {showHolds && (
        <div className="modal" style={{ zIndex: 2000 }}>
          <div className="modal-content" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h2>Held bills</h2>
              <button type="button" className="modal-close" onClick={() => setShowHolds(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {heldBills.length === 0 ? (
                <p className="pos-empty">No held bills</p>
              ) : (
                <div className="pos-holds-list">
                  {[...heldBills].reverse().map((bill) => {
                    const items = Array.isArray(bill.cart) ? bill.cart.length : 0;
                    const qty = Array.isArray(bill.cart)
                      ? bill.cart.reduce((s, i) => s + (parseFloat(i.quantity) || 0), 0)
                      : 0;
                    return (
                      <div key={bill.id} className="pos-hold-row">
                        <div>
                          <strong>{bill.customer_name || 'No customer'}</strong>
                          <div className="pos-hold-meta">
                            {items} line{items === 1 ? '' : 's'} · {qty} item{qty === 1 ? '' : 's'} ·{' '}
                            {bill.payment_status || 'paid'}
                            {bill.created_at ? ` · ${new Date(bill.created_at).toLocaleString()}` : ''}
                          </div>
                        </div>
                        <div className="pos-hold-actions">
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => deleteHold(bill.id)}>
                            Delete
                          </button>
                          <button type="button" className="btn btn-primary btn-sm" onClick={() => resumeHold(bill)}>
                            Resume
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showCustomerModal && (
        <div className="modal" style={{ zIndex: 2000 }}>
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>Add New Customer</h2>
              <button type="button" className="close-btn" onClick={() => setShowCustomerModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleCustomerSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={customerFormData.name}
                  onChange={(e) => setCustomerFormData({ ...customerFormData, name: e.target.value })}
                  required
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={customerFormData.email}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Phone / Mobile *</label>
                  <input
                    type="text"
                    value={customerFormData.phone}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, phone: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={customerFormData.address}
                  onChange={(e) => setCustomerFormData({ ...customerFormData, address: e.target.value })}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={customerFormData.city}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, city: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={customerFormData.state}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, state: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Zip Code</label>
                  <input
                    type="text"
                    value={customerFormData.zip_code}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, zip_code: e.target.value })}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    value={customerFormData.country}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, country: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Tax ID</label>
                  <input
                    type="text"
                    value={customerFormData.tax_id}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, tax_id: e.target.value })}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCustomerModal(false)}
                  disabled={savingCustomer}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={savingCustomer}>
                  {savingCustomer ? 'Saving...' : 'Save Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default POS;
